import { PaginationStyle } from '../../../controllers/gs-pagination/models/pagination-style';
import { DesignerColorType } from '../../basic';
import { ResponsiveAttribute } from '../../basic/responsive-attribute';
import { IFontConfigStyle, IFontConfigWeight } from '../../build-template/template-config';
import { ColorType, IIcon } from '../../configs';
import { ISides, TextTransformType } from '../../configs/config';
import { PageComponentType } from './component-types';

export enum GsStructTarget {
  _blank = 0,
  _self = 1
}

export enum GsStructHorizontalAlignment {
  left = 0,
  center = 1,
  right = 2
}

export enum GsStructVerticalAlignment {
  top = 0,
  middle = 1,
  bottom = 2
}

export enum GsStructTextAlignment {
  left = 0,
  center = 1,
  right = 2,
  justify = 3
}

export enum GsStructColorClass {
  primary = 0,
  secondary = 1,
  tertiary = 2,
  light = 3,
  medium = 4,
  dark = 5
}

export enum GsStructBorderStyle {
  none = 0,
  solid = 1,
  dotted = 2,
  dashed = 3,
  double = 4
}

export enum GsStructBorderRadius {
  none = 0,
  sm = 1,
  regular = 2,
  md = 3,
  lg = 4,
  xl = 5,
  xxl = 6,
  xxxl = 7,
  full = 8
}

export enum GsStructBorderWidth {
  zeroPixel = 0,
  onePixel = 1,
  twoPixels = 2,
  fourPixels = 3,
  eightPixels = 4,
  tenPixels = 5,
  twelvePixels = 6,
  fourteenPixels = 7,
  sixteenPixels = 8,
  eighteenPixels = 9,
  twentyPixels = 10
}

export enum SharedItemOverlayDisplayMode {
  never = 0,
  onlyWithText = 1,
  always = 2
}

export enum SearchOrderType {
  Relevance = 0,
  Title = 1,
  MinKeyPrice = 2,
  Priority = 3,
  Title2 = 4,
  MaxKeyPrice = 5,
  CreatedOn = 6,
  ModifiedOn = 7,
  Custom = 8,
  Custom2 = 9,
  TopArticles = 10,
  Height = 11,
  Circumference = 12,
  CircumferenceHeight = 13,
  LatestDate = 14,
  EarliestDate = 15
}

export interface GsStructHeader<T> {
  code: T;
  /**
   * Signals the backend that the render is requested from the Designer, so it returns editable
   * form dialogs instead of runtime redirects (e.g. the login form for an already-logged-in
   * session). Only honored server-side for sessions holding page-design rights.
   */
  designMode?: boolean;
  /**
   * Column span (out of 12) of the page block the component sits in. The single-component render
   * builds its own synthetic block and would otherwise assume full width, producing image variants
   * the block can never display. Clamped to 1-12 server-side.
   */
  blockColumns?: number | ResponsiveAttribute<number>;
  /**
   * Marks the render as the visitor actually seeing the component, which is what the backend gates
   * the view_item, view_cart and begin_checkout tracking events on. A re-render triggered by an
   * action (cart update, variant switch) is not a view and must leave the header off.
   */
  viewRender?: boolean;
}

export interface GsStructComponent<RenderType, ComponentType = PageComponentType> {
  /** The clientside-id of the component */
  clientId: string;
  /** The type of component e.g. text or image and so on */
  componentType: ComponentType;
  /** The padding values for the component */
  padding: ResponsiveAttribute<ISides>;
  /** The maximum width of the component */
  maxWidth?: ResponsiveAttribute<number>;
  /**
   * The horizontal alignment of the component inside the block
   * @deprecated implemented via block
   */
  horizontalAlignment?: GsStructHorizontalAlignment;
  /**
   * The vertical alignment of the component inside the block
   * @deprecated implemented via block
   */
  verticalAlignment?: GsStructVerticalAlignment;
  /** All data and information to show/render the component */
  render?: RenderType;
  /** CS4-1929: backend omitted `render`; fetched lazily on viewport entry. Distinguishes "intentionally empty" from error. */
  lazy?: boolean;
  /** Responsive configuration to control component visibility across different breakpoints */
  shown?: ResponsiveAttribute<boolean>;
  /** Date when component start to be active */
  activeFrom?: Date;
  /** Date when component stops to be active */
  activeUntil?: Date;
  /** Maximum cache age (in hours) before this component is re-rendered server-side. Fractional values are allowed (e.g. 0.5 = 30 minutes). */
  maxCacheHours?: number;
  /** Template Fonts */
  font?: GsStructFont;
  /** Error information */
  error?: GsStructComponentError;
  /** Designer badge information, if set, a badge will be shown in the corner of the component in the designer. */
  info?: IDesignerBadge;
  /** Anchor name rendered as the component's DOM id, so menu items with a `#anchor` in their url can jump to it */
  anchor?: string;
}

export interface GsStructComponentError {
  /** Error message to display // describes the error */
  message: string;
  /** Unique id of the error for tracking inside BC */
  errorID: string;
}

export interface IDesignerBadge {
  text: string;
  color?: DesignerColorType | ColorType;
  icon?: IIcon;
}

export interface GsStructFont {
  family: string;
  style: IFontConfigStyle;
  weight: IFontConfigWeight;
  /** Text transform (uppercase, lowercase, capitalize, none) */
  textTransform?: TextTransformType;
  /** Line height in em (e.g. 1.4); unset = browser default */
  lineHeight?: number;
}

export interface GsStructPageItems<SortOrderType = SearchOrderType> {
  /** Show pagination/navigation of pages or not */
  withPagination: boolean;
  /** The style of the pagination/navigation of pages */
  paginationType?: PaginationStyle;
  /** Current page index, starts with 0 */
  pageIndex: number;
  /** Amount of items for the pagesize */
  pageSize: number;
  /** Items per row (Amount of cols) on desktop */
  itemsRowDesktop: number;
  /** Items per row (Amount of cols) on tablet */
  itemsRowTablet: number;
  /** Items per row (Amount of cols) on mobile */
  itemsRowMobile: number;
  /** Search string for filtering the results (base64 encoded) */
  search?: string;
  /** The order of the items */
  sortOrderType?: SortOrderType;
}

export function isStructPageItems(obj: unknown): obj is GsStructPageItems {
  return typeof obj === 'object' && obj !== null && 'itemsRowDesktop' in obj && 'itemsRowTablet' in obj && 'itemsRowMobile' in obj;
}

export interface GsStructSwiper {
  /** Tease next Item? (Shows half item) */
  sliderTeaseItem: boolean;
  /** Tease item size in percent (10-90) */
  sliderTeaseItemSize?: number;
  /** Items to slide/move per turn */
  sliderAmountGroup: number;
  /** Speed in ms */
  sliderSpeed: number;
  /** Show/hide navigation */
  sliderNavigation: boolean;
  /** Autoplay or not */
  sliderAutoplay: boolean;
  /** Delay in per change slide/move in ms */
  sliderDelay: number;
  /** To loop items or not */
  sliderLoop: boolean;
  /** Show/hide pagination */
  sliderPagination: boolean;
  /** Show/hide scrollbar */
  sliderScrollbar: boolean;
  /** Direction of the slider */
  sliderDirection: SliderDirection;
  /** Items per col (Amount of rows) */
  sliderColumns: number;
  /** Space between slides */
  sliderSpaceBetween: number;
}

export enum SliderDirection {
  horizontal = 0,
  vertical = 1
}

export function isStructSwiper(obj: unknown): obj is GsStructSwiper {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'sliderAmountGroup' in obj &&
    'sliderAutoplay' in obj &&
    'sliderLoop' in obj &&
    'sliderPagination' in obj &&
    'sliderScrollbar' in obj &&
    'sliderSpaceBetween' in obj
  );
}
